<script lang="ts" setup>
import { sleep } from '@shared/timers'
import { faCircleUser } from '@fortawesome/pro-regular-svg-icons'
import { useMotion } from '@vueuse/motion'
import { shuffle } from 'lodash-es'

import newMiscAwardIcon from '@consumer/assets/icons/new-misc/award.svg?svgicon'
import GiftCollage from './GiftCollage.vue'

const thankYouNote = ref<HTMLElement>()
const thankYouMotion = useMotion(thankYouNote, {
  initial: {
    opacity: 0,
    transition: { duration: 0 },
  },
  reveal: {
    opacity: 1.0,
    transition: { ease: 'easeInOut' },
  },
})

const emoji1 = ref<HTMLElement>()
const emoji1Motion = useMotion(emoji1, {
  initial: {
    opacity: 0,
    transition: { duration: 0 },
  },
  reveal: {
    opacity: 0.5,
    transition: { ease: 'easeInOut' },
  },
})

const emoji2 = ref<HTMLElement>()
const emoji2Motion = useMotion(emoji2, {
  initial: {
    opacity: 0,
    transition: { duration: 0 },
  },
  reveal: {
    opacity: 0.75,
    transition: { ease: 'easeInOut' },
  },
})

const emoji3 = ref<HTMLElement>()
const emoji3Motion = useMotion(emoji3, {
  initial: {
    opacity: 0,
    transition: { duration: 0 },
  },
  reveal: {
    opacity: 1,
    transition: { ease: 'easeInOut' },
  },
})

const emoji4 = ref<HTMLElement>()
const emoji4Motion = useMotion(emoji4, {
  initial: {
    opacity: 0,
    transition: { duration: 0 },
  },
  reveal: {
    opacity: 1,
    transition: { ease: 'easeInOut' },
  },
})

const emoji5 = ref<HTMLElement>()
const emoji5Motion = useMotion(emoji5, {
  initial: {
    opacity: 0,
    transition: { duration: 0 },
  },
  reveal: {
    opacity: 1,
    transition: { ease: 'easeInOut' },
  },
})

const emoji6 = ref<HTMLElement>()
const emoji6Motion = useMotion(emoji6, {
  initial: {
    opacity: 0,
    transition: { duration: 0 },
  },
  reveal: {
    opacity: 1,
    transition: { ease: 'easeInOut' },
  },
})

const emoji7 = ref<HTMLElement>()
const emoji7Motion = useMotion(emoji7, {
  initial: {
    opacity: 0,
    transition: { duration: 0 },
  },
  reveal: {
    opacity: 1,
    transition: { ease: 'easeInOut' },
  },
})

defineExpose({
  reset: () => {
    thankYouMotion.apply('initial')
    emoji1Motion.apply('initial')
    emoji2Motion.apply('initial')
    emoji3Motion.apply('initial')
    emoji4Motion.apply('initial')
    emoji5Motion.apply('initial')
    emoji6Motion.apply('initial')
    emoji7Motion.apply('initial')
  },
  play: async () => {
    thankYouMotion.apply('reveal')
    await sleep(500)
    for (const motion of shuffle([
      emoji1Motion,
      emoji2Motion,
      emoji3Motion,
      emoji4Motion,
      emoji5Motion,
      emoji6Motion,
      emoji7Motion,
    ])) {
      await motion.apply('reveal')
    }
  },
})
</script>

<template>
  <div style="font-size: 36px;">
    <div class="absolute left-1/2 transform -translate-x-1/2 scale-80" style="top: 15px;">
      <GiftCollage :animationSpeed="0"/>
    </div>

    <div
      class="
        absolute left-1/2 transform transition-all -translate-x-1/2 w-full
        flex justify-center items-center gap-2 text-sm
      "
      style="top: 400px;"
    >
      <GIcon :svg="newMiscAwardIcon" size="30px"/>
      100% satisfaction guaranteed
    </div>

    <div
      ref="emoji7"
      class="absolute z-2 bg-white rounded-v2lg shadow-v2md p-3"
      style="right: 40px; top: 0;"
      :style="{ scale: 1, rotate: '-30deg' }"
    >
      🔥
    </div>
    <div
      ref="emoji6"
      class="absolute z-2 bg-white rounded-v2lg shadow-v2md p-3"
      style="right: 10px; top: 70px;"
      :style="{ scale: .9, rotate: '20deg' }"
    >
      🥰
    </div>
    <div
      ref="emoji5"
      class="absolute z-2 bg-white rounded-v2lg shadow-v2md p-3"
      style="right: 30px; top: 140px;"
      :style="{ scale: .8, rotate: '-20deg' }"
    >
      ❤️
    </div>
    <div
      ref="emoji4"
      class="absolute z-2 bg-white rounded-v2lg shadow-v2md p-3"
      style="right: 5px; top: 210px;"
      :style="{ scale: .7, rotate: '25deg' }"
    >
      🤩
    </div>

    <div
      ref="emoji3"
      class="absolute z-2 bg-white rounded-v2lg shadow-v2md p-3"
      style="right: 40px; top: 260px;"
      :style="{ scale: .6, rotate: '-15deg' }"
    >
      🙏
    </div>
    <div
      ref="emoji2"
      class="absolute z-2 bg-white rounded-v2lg shadow-v2md p-3"
      style="right: 20px; top: 310px;"
      :style="{ scale: .5, rotate: '35deg' }"
    >
      🤗
    </div>
    <div
      ref="emoji1"
      class="absolute z-2 bg-white rounded-v2lg shadow-v2md p-3"
      style="right: 50px; top: 340px;"
      :style="{ scale: .4, rotate: '-20deg' }"
    >
      💛
    </div>

    <div
      ref="thankYouNote"
      class="text-sm bg-white text-navy font-medium
        text-left rounded-v2md p-2 flex gap-2 items-center
        px-3 py-4
        absolute left-[15px] lg:-left-[50px]
        shadow-v2md
      "
      style="top: 260px; width: 200px;"
    >
      <GIcon :icon="faCircleUser" class="text-v2-active" size="lg"/>
      Thank you for such a thoughtful gift! 😀
    </div>
  </div>
</template>
